import { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import ErrorReporter from 'src/monitoring/errorReporter';
import { useAlert } from 'src/contexts/alert-context';
import { Tabs } from 'src/modules/shared/components/atoms/tabs/tabs';
import { Tab } from 'src/modules/shared/components/atoms/tabs/tab';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import Loading from 'src/modules/shared/components/atoms/loading';
import { CountBadge } from 'src/modules/shared/components/atoms/count-badge';
import { Route as CommunicationsIndexRoute } from 'src/routes/communications/index';
import { communicationsSearchSchema } from 'src/modules/shared/schemas/communications-search.schema';
import {
  useNotificationsInbox,
  useNotificationsMarkRead,
} from '../../hooks/api/notifications';
import {
  hasWebPushVapidKey,
  isWebPushSupported,
  registerWebPushSubscription,
} from '../../hooks/push/use-web-push';
import type { NotificationInboxItem } from '../../types/notification.types';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { getWebPushActivationAlert } from '../../utils/web-push-activation-alert.util';
import { NotificationListItem } from '../molecules/notification-list-item';
import { MessageNotificationCard } from '../molecules/message-notification-card';
import { groupNotificationItems } from '../../utils/notification-group.util';

type InboxFilter = 'all' | 'unread';
type BrowserNotificationPermission = 'default' | 'denied' | 'granted';

interface NotificationInboxProps {
  onClose?: () => void;
}

export const NotificationInbox: React.FC<NotificationInboxProps> = ({
  onClose,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: loadingAuth } = useAuth0();
  const { showAlert } = useAlert();
  const { t } = useI18n();
  const [filter, setFilter] = useState<InboxFilter>('all');
  const [pushPermission, setPushPermission] = useState<
    BrowserNotificationPermission | 'unsupported'
  >('default');
  const [requestingPush, setRequestingPush] = useState(false);
  const [activationFailedAfterPermission, setActivationFailedAfterPermission] =
    useState(false);
  const hasVapidKey = hasWebPushVapidKey();

  const {
    items,
    unreadCount,
    isLoading,
    error,
    loadMore,
    hasNextPage,
    loadingMore,
  } = useNotificationsInbox({
    read: filter === 'unread' ? false : undefined,
    limit: 25,
  });

  const markReadMutation = useNotificationsMarkRead();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (!isWebPushSupported()) {
      setPushPermission('unsupported');
      return;
    }
    setPushPermission(Notification.permission);
  }, []);

  const hasUnread = useMemo(() => {
    if (typeof unreadCount === 'number') {
      return unreadCount > 0;
    }
    return items.some((item) => !item.readAt);
  }, [items, unreadCount]);

  const handleMarkAllRead = () => {
    markReadMutation.mutate({ readAll: true });
  };

  const handleMarkItemRead = (item: NotificationInboxItem) => {
    if (item.readAt || markReadMutation.isPending) {
      return;
    }

    markReadMutation.mutate({ inboxIds: [item.id] });
  };

  const handleEnablePush = async () => {
    if (!isAuthenticated || loadingAuth) {
      return;
    }

    setRequestingPush(true);
    try {
      const result = await registerWebPushSubscription({
        requestPermission: true,
      });
      if (result === 'registered') {
        setActivationFailedAfterPermission(false);
        void queryClient.invalidateQueries({
          queryKey: ['notifications-endpoints'],
        });
      } else {
        setActivationFailedAfterPermission(
          Notification.permission === 'granted'
        );
      }

      const alert = getWebPushActivationAlert(result);
      showAlert(t(alert.messageKey), alert.type);
    } catch (error) {
      ErrorReporter.sendException(error);
      showAlert(t('settings.notifications.alerts.error'), 'error');
    } finally {
      if (typeof window !== 'undefined' && 'Notification' in window) {
        setPushPermission(Notification.permission);
      }
      setRequestingPush(false);
    }
  };

  const handleOpenNotification = (
    item: NotificationInboxItem,
    entries: NotificationInboxItem[] = [item]
  ) => {
    const inboxIds = entries
      .filter((entry) => !entry.readAt)
      .map((entry) => entry.id);
    if (inboxIds.length > 0) markReadMutation.mutate({ inboxIds });
    if (item.metadata?.message && item.metadata.funnelConversationId) {
      void navigate({
        to: CommunicationsIndexRoute.to,
        search: communicationsSearchSchema.parse({
          conversationId: item.metadata.funnelConversationId,
        }),
      });
      onClose?.();
      return;
    }

    const deepLink = item.metadata?.deepLink;
    if (deepLink) {
      if (/^(https?:)?\/\//i.test(deepLink)) {
        window.open(deepLink, '_blank', 'noopener,noreferrer');
      } else {
        void navigate({ to: deepLink });
      }
      onClose?.();
      return;
    }

    if (item.metadata?.funnelConversationId) {
      void navigate({
        to: CommunicationsIndexRoute.to,
        search: communicationsSearchSchema.parse({
          conversationId: item.metadata.funnelConversationId,
        }),
      });
      onClose?.();
      return;
    }

    void navigate({
      to: CommunicationsIndexRoute.to,
      search: communicationsSearchSchema.parse({}),
    });
    onClose?.();
  };

  return (
    <div className="flex flex-col gap-4">
      {hasVapidKey &&
        pushPermission !== 'unsupported' &&
        (pushPermission !== 'granted' || activationFailedAfterPermission) && (
          <Alert
            appearance="soft"
            variant="primary"
            message={
              <div className="flex flex-col gap-3">
                <span>
                  Activa las notificaciones del navegador para recibir alertas
                  en tiempo real.
                </span>
                <div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleEnablePush}
                    disabled={!isAuthenticated || loadingAuth}
                    loading={requestingPush}
                  >
                    Activar notificaciones
                  </Button>
                </div>
              </div>
            }
          />
        )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Tabs
            value={filter}
            onChange={(value) => setFilter(value as InboxFilter)}
            appearance="soft"
          >
            <Tab value="all" label="Todas" />
            <Tab value="unread" label="No leídas" />
          </Tabs>
          {typeof unreadCount === 'number' && unreadCount > 0 && (
            <CountBadge count={unreadCount} size="xs" variant="primary" />
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleMarkAllRead}
          disabled={!hasUnread || markReadMutation.isPending}
        >
          Marcar todo como leído
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-10">
          <Loading size="lg" />
        </div>
      )}

      {error && <ErrorState title="No se pudieron cargar las notificaciones" />}

      {!isLoading && !error && items.length === 0 && (
        <EmptyState
          title="No hay notificaciones"
          description="Cuando tengas nuevas notificaciones aparecerán aquí."
          appearance="soft"
          variant="neutral"
        />
      )}

      {!isLoading && !error && items.length > 0 && (
        <div className="flex flex-col gap-3">
          {groupNotificationItems(items).map((group) =>
            group.latest.metadata?.message ? (
              <MessageNotificationCard
                key={group.key}
                group={group}
                onOpen={() => handleOpenNotification(group.latest, group.items)}
                onMarkRead={() =>
                  markReadMutation.mutate({
                    inboxIds: group.items
                      .filter((item) => !item.readAt)
                      .map((item) => item.id),
                  })
                }
                markReadDisabled={markReadMutation.isPending}
              />
            ) : (
              <NotificationListItem
                key={group.key}
                item={group.latest}
                onClick={(item) => handleOpenNotification(item)}
                onMarkRead={handleMarkItemRead}
                markReadDisabled={markReadMutation.isPending}
              />
            )
          )}
          {hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                size="sm"
                onClick={loadMore}
                loading={loadingMore}
                disabled={loadingMore}
              >
                Cargar más
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
